import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { assignMissionPath } from "@/routes"
import type { AssignmentMode } from "@/types/mission-assign"

/**
 * MissionAssignConfirmDialog — wraps Inertia `<Form>` for the confirm
 * POST. Sends top-level params: `mode`, `segment_id`, `crew_membership_ids[]`
 * (NEVER nested under `assignment[…]` per TRD §4 lock).
 *
 * Uses Inertia `<Form>` from `@inertiajs/react` (Crew rule — never the
 * shadcn `Form`/react-hook-form/zod). Hidden inputs carry the per-mode
 * selection; the submit button shows a spinner while
 * `processing === true`.
 */
interface MissionAssignConfirmDialogProps {
  missionId: number
  mode: AssignmentMode
  segmentId: number | null
  handpickIds: number[]
  /** Number of new rows the confirm will insert — drives copy. */
  newCount: number
  /**
   * OKS-936 — `preview.no_ship_available`. When false the checkbox and its
   * helper are ABSENT, not disabled (UX §4 empty state): a control that
   * cannot apply should not advertise itself.
   */
  noShipAvailable: boolean
  /**
   * Disabled when `can_confirm === false` (no targets / not allowed) OR
   * the selection exceeds the remaining gift slots (OKS-494).
   */
  disabled?: boolean
  /**
   * OKS-494 — id of the element describing WHY the trigger is disabled
   * (the over-cap gift-slots warning), wired as `aria-describedby` so
   * assistive tech ties the disabled CTA to the reason (UX Spec §6).
   */
  describedBy?: string
  testId?: string
}

export function MissionAssignConfirmDialog({
  missionId,
  mode,
  segmentId,
  handpickIds,
  newCount,
  noShipAvailable,
  disabled = false,
  describedBy,
  testId = "mission-assign-confirm-dialog",
}: MissionAssignConfirmDialogProps) {
  const { t } = useTranslation()
  // OKS-936 — component-local, submitted with the confirm POST and
  // deliberately NOT reflected in the URL (unlike `mode` / `segment_id`):
  // it is a one-shot decision about this batch, not a view. Lost with the
  // dialog on a 422, which UX §4 accepts — the brand re-picks.
  const [noShip, setNoShip] = useState(false)

  return (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            type="button"
            disabled={disabled}
            aria-describedby={describedBy}
            data-testid="mission-assign-confirm-trigger"
          >
            {t("missions.assign.confirm.cta")}
          </Button>
        }
      />
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{t("missions.assign.confirm.title")}</DialogTitle>
          <DialogDescription>
            {t("missions.assign.confirm.body", { count: newCount })}
          </DialogDescription>
        </DialogHeader>

        <Form
          action={assignMissionPath(missionId)}
          method="post"
          options={{ preserveScroll: true }}
          data-testid="mission-assign-confirm-form"
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="mode" value={mode} />
              {mode === "segment" && segmentId !== null && (
                <input
                  type="hidden"
                  name="segment_id"
                  value={String(segmentId)}
                />
              )}
              {mode === "handpick" &&
                handpickIds.map((id) => (
                  <input
                    key={id}
                    type="hidden"
                    name="crew_membership_ids[]"
                    value={String(id)}
                  />
                ))}

              {/*
                OKS-936 — "they already have it, don't ship" (UX §2).
                Default unchecked ALWAYS, including when every new creator
                carries a recency chip (PRD OQ-3): pre-checking would make
                the expensive direction the default, and OKS-935's posture
                is warn-never-block.

                The wire is `no_ship=1` when checked, key ABSENT otherwise —
                so the hidden input is rendered conditionally rather than
                bound to the Base UI checkbox's own form value. The server
                coerces with ActiveModel::Type::Boolean and ignores it
                entirely when the shortcut does not apply, so a forged POST
                cannot create a no-ship collab on an unguarded mission.
              */}
              {noShipAvailable && (
                <div className="flex items-start gap-2 py-2">
                  <Checkbox
                    id="mission-assign-no-ship"
                    checked={noShip}
                    onCheckedChange={(checked) => setNoShip(checked === true)}
                    aria-describedby="mission-assign-no-ship-helper"
                    disabled={processing}
                    className="mt-0.5"
                    data-testid="mission-assign-no-ship"
                  />
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor="mission-assign-no-ship"
                      className="text-sm leading-snug font-medium"
                    >
                      {t("missions.assign.no_ship.label")}
                    </Label>
                    <p
                      id="mission-assign-no-ship-helper"
                      className="text-muted-foreground text-xs"
                    >
                      {t("missions.assign.no_ship.helper", { count: newCount })}
                    </p>
                  </div>
                </div>
              )}
              {noShipAvailable && noShip && (
                <input type="hidden" name="no_ship" value="1" />
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  data-testid="mission-assign-confirm-cancel"
                  onClick={(e) => {
                    // Close the dialog without submitting. shadcn Dialog
                    // handles ESC + outside-click; this is the explicit
                    // Cancel CTA — find the closest dialog and dispatch
                    // a close event by clicking the auto-rendered close
                    // affordance. Simpler: bubble a custom event.
                    const dialog = (e.currentTarget as HTMLElement).closest(
                      "[role='dialog']",
                    )
                    const closeBtn = dialog?.querySelector<HTMLButtonElement>(
                      "[data-radix-dialog-close], [aria-label='Close']",
                    )
                    closeBtn?.click()
                  }}
                >
                  {t("missions.assign.confirm.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={processing}
                  data-testid="mission-assign-confirm-submit"
                  autoFocus
                >
                  {processing && (
                    <Spinner className="mr-1 size-4" aria-hidden="true" />
                  )}
                  {t("missions.assign.confirm.cta")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
